import { useApolloClient } from "@apollo/client";
import { isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import {
  type OrganizationMessageFieldsFragment,
  type OrganizationMessagesQuery,
  OrganizationMessagesDocument,
  type OrganizationMessagesQueryVariables,
  type OrganizationMessageView,
  useOrganizationMessagesQuery,
} from "../../generated/graphql";

export const MESSAGE_PAGE_SIZE = 50;

type OlderPages = {
  /** The first page these follow; a refetch or another view starts over. */
  readonly after: OrganizationMessagesQuery;
  readonly messages: readonly OrganizationMessageFieldsFragment[];
  readonly endCursor: string | null;
  readonly hasNextPage: boolean;
};

const nodesOf = (data: OrganizationMessagesQuery) =>
  data.organizationMessages.edges.map((edge) => edge.node);

/**
 * One view of the staff list, paged with the cursor. The app runs queries
 * without a cache, so older pages are kept here and dropped whenever the first
 * page reloads (after a send, an edit, or a tab change), which keeps every row
 * as fresh as the first page.
 */
const useMessagePages = (view: OrganizationMessageView | undefined) => {
  const client = useApolloClient();
  const { data, error } = useOrganizationMessagesQuery({
    variables: { first: MESSAGE_PAGE_SIZE, view },
    errorPolicy: "all",
  });
  const [older, setOlder] = useState<OlderPages | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  const current = older !== null && older.after === data ? older : null;
  const messages = useMemo(
    () =>
      isNil(data)
        ? []
        : [...nodesOf(data), ...(current === null ? [] : current.messages)],
    [data, current],
  );
  const pageInfo = data?.organizationMessages.pageInfo;
  const hasMore = current?.hasNextPage ?? pageInfo?.hasNextPage ?? false;
  const endCursor = current?.endCursor ?? pageInfo?.endCursor ?? null;

  const loadMore = useCallback(async () => {
    if (isNil(data) || endCursor === null) {
      return;
    }
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const result = await client.query<
        OrganizationMessagesQuery,
        OrganizationMessagesQueryVariables
      >({
        query: OrganizationMessagesDocument,
        variables: { first: MESSAGE_PAGE_SIZE, after: endCursor, view },
        fetchPolicy: "no-cache",
      });
      setOlder({
        after: data,
        messages: [...(current?.messages ?? []), ...nodesOf(result.data)],
        endCursor: result.data.organizationMessages.pageInfo.endCursor ?? null,
        hasNextPage: result.data.organizationMessages.pageInfo.hasNextPage,
      });
    } catch (error_) {
      setLoadMoreError(
        error_ instanceof Error ? error_.message : String(error_),
      );
    } finally {
      setLoadingMore(false);
    }
  }, [client, current, data, endCursor, view]);

  return { messages, error, hasMore, loadingMore, loadMoreError, loadMore };
};

export default useMessagePages;
